import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LogOut, ChevronDown, Check } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { ChangePassword } from "../../components/ChangePassword";

// 7 top-level tabs. Groups open a portaled, fixed-position dropdown so
// they are never clipped by the nav's horizontal scroll container.
const NAV = [
  { to: "/admin", label: "Overview", end: true },
  {
    label: "Academy Management",
    key: "academy",
    children: [
      { to: "/admin/players", label: "Players" },
      { to: "/admin/documents", label: "Documents" },
      { to: "/admin/payments", label: "Payments" },
      { to: "/admin/dues", label: "Dues" },
      { to: "/admin/reminders", label: "Reminders" },
      { to: "/admin/centres", label: "Centres" },
      { to: "/admin/coaches", label: "Coaches" },
    ],
  },
  { to: "/admin/attendance", label: "Attendance" },
  { to: "/admin/trials", label: "Leads" },
  {
    label: "Player Development",
    key: "development",
    children: [
      { to: "/admin/resource-bank", label: "Resource Bank" },
      { to: "/admin/tournaments", label: "Tournaments" },
      { to: "/admin/reports", label: "Progress Reports" },
    ],
  },
  { to: "/admin/feedback", label: "Feedback" },
  { to: "/admin/policies", label: "Policies" },
];

const tabBase =
  "px-4 py-3 text-xs uppercase tracking-widest font-semibold whitespace-nowrap border-b-2 transition-colors duration-200";
const tabIdle = "border-transparent text-white/50 hover:text-white";
const tabActive = "border-[#D4AF37] text-[#D4AF37]";

const isChildActive = (children, pathname) =>
  children.some((c) => pathname === c.to || pathname.startsWith(c.to + "/"));

const testId = (label) =>
  `admin-tab-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

const GroupTab = ({ group, pathname }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const active = isChildActive(group.children, pathname);
  const navigate = useNavigate();

  const measure = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const menuWidth = Math.min(300, vw - 24); // 12px gutter both sides on mobile
    // Clamp horizontally so menu never overflows the viewport.
    let left = r.left;
    if (left + menuWidth > vw - 12) left = vw - menuWidth - 12;
    if (left < 12) left = 12;
    setPos({ top: r.bottom, left, width: menuWidth });
  };

  useLayoutEffect(() => { if (open) measure(); }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onScrollOrResize = () => measure();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open]);

  // Close menu on route change
  useEffect(() => { setOpen(false); }, [pathname]);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${tabBase} inline-flex items-center gap-1.5 ${active ? tabActive : tabIdle}`}
        data-testid={testId(group.label)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {group.label}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            data-testid={`${testId(group.label)}-menu`}
            style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width }}
            className="z-[70] bg-[#0A0A0A] border border-[#D4AF37]/50 shadow-[0_20px_60px_rgba(0,0,0,0.85)]"
          >
            <div className="px-4 py-2.5 text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]/70 border-b border-white/10">
              {group.label}
            </div>
            <ul className="py-1">
              {group.children.map((c) => {
                const selected = pathname === c.to || pathname.startsWith(c.to + "/");
                return (
                  <li key={c.to}>
                    <button
                      role="menuitem"
                      onClick={() => go(c.to)}
                      data-testid={testId(c.label)}
                      className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-[13px] tracking-wide transition-colors ${
                        selected
                          ? "text-[#D4AF37] bg-[#D4AF37]/10"
                          : "text-white/85 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      <span>{c.label}</span>
                      {selected && <Check size={14} className="text-[#D4AF37]" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body
        )}
    </>
  );
};

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-layout">
      <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="" className="h-16 w-auto" data-testid="admin-header-logo" />
            <span className="font-heading font-black tracking-tighter text-lg leading-none">
              ZAVI<span className="text-[#D4AF37]">FC</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-white/40 text-xs">{user?.email}</span>
              <ChangePassword testid="admin-change-password" />
            </div>
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              className="flex items-center gap-2 text-white/60 hover:text-[#D4AF37] text-xs uppercase tracking-widest transition-colors duration-200"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {/* 7 top-level tabs. Grouped ones open a portaled fixed-position menu. */}
        <nav className="max-w-7xl mx-auto px-4 lg:px-8 flex gap-1 overflow-x-auto" data-testid="admin-tabs">
          {NAV.map((item) =>
            item.children ? (
              <GroupTab key={item.key} group={item} pathname={pathname} />
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={testId(item.label)}
                className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabIdle}`}
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
};
